import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import MessageDialog from "../../components/MessageDialog";
import StorageService from "../../services/StorageService";
import pettyCashService from "../../services/pettyCashService";
import PettyCashPaymentsReportService from "../../services/pettyCashPaymentsReportService";
import utility from "../../utils/utility";
import XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../../assets/images/deviops_logo.png";
import { addPdfLogo, getPdfLogoDataUrl } from "../../utils/pdfLogo";

export default function PettyCashPaymentsReport() {
  const activeCompany = StorageService.activeCompany();
  const companyId = activeCompany?.companyId || "";

  const [employeeId, setEmployeeId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [employees, setEmployees] = useState([]);
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState({
    totalPayments: 0,
    totalExp: 0,
    balance: 0,
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("error");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const formatInputDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getCurrentMonthDateRange = () => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      startDate: formatInputDate(startDate),
      endDate: formatInputDate(endDate),
    };
  };

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
  };

  const formatProjectDisplay = (project) => {
    if (!project) return "-";

    const projectName = project.projectName || project.name || "";
    const categoryName = project.projectCategoryName || project.categoryName || project.category || "";
    const projectCode = project.projectCode || "";
    const categoryInitial = String(categoryName).trim().charAt(0).toUpperCase();

    if (projectName) {
      if (categoryInitial) {
        return `${categoryInitial}*${projectName}`;
      }

      if (projectCode) {
        const codeInitial = String(projectCode).trim().charAt(0).toUpperCase();
        return `${codeInitial}*${projectName}`;
      }

      return projectName;
    }

    if (projectCode) {
      const codeInitial = String(projectCode).trim().charAt(0).toUpperCase();
      return `${codeInitial}*`;
    }

    return categoryInitial ? `${categoryInitial}*` : "-";
  };

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    const formatted = Math.abs(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return amount < 0 ? `(${formatted})` : formatted;
  };

  const getEmployeeLabel = () => {
    if (!employeeId) return "All";
    const found = employees.find((employee) => String(employee.id) === String(employeeId));
    return found ? `${found.firstName || ""} ${found.lastName || ""}`.trim() : employeeId;
  };

  const loadDropdowns = async () => {
    try {
      const employeeRes = await pettyCashService.getEmployeesByCompany(companyId);
      setEmployees(employeeRes?.data || []);
    } catch (error) {
      console.error(error);
      showMessage("Failed to load employee dropdowns");
    }
  };

  const loadReport = async (pageNumber = page) => {
    if (startDate && !endDate) {
      showMessage("Start Date and End Date are required");
      return;
    }

    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      showMessage("Start Date should be less than or equal to End Date");
      return;
    }

    setLoading(true);
    try {
      setReports([]);
      setSummary({
        totalPayments: 0,
        totalExp: 0,
        balance: 0,
      });
      setPage(1);
      const res = await PettyCashPaymentsReportService.getReport({
        companyId: companyId || undefined,
        employeeId: employeeId || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        page: pageNumber,
        limit,
      });

      setReports(res?.data || []);
      setSummary(res?.summary || {});
      setTotalPages(res?.pagination?.totalPages || 0);
      setTotalRecords(res?.pagination?.total || 0);
      setPage(pageNumber);
    } catch (error) {
      console.error(error);
      setReports([]);
      showMessage("Error fetching petty cash payments report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) {
      loadDropdowns();
      const { startDate: monthStart, endDate: monthEnd } = getCurrentMonthDateRange();
      setStartDate(monthStart);
      setEndDate(monthEnd);
    }
  }, [companyId]);

  const handleClearFilters = () => {
    setEmployeeId("");
    const { startDate: monthStart, endDate: monthEnd } = getCurrentMonthDateRange();
    setStartDate(monthStart);
    setEndDate(monthEnd);
    setReports([]);
    setSummary({
      totalPayments: 0,
      totalExp: 0,
      balance: 0,
    });
    setPage(1);
    setTotalPages(0);
    setTotalRecords(0);
    loadReport(1);
  };

  const getAllReportData = async () => {
    try {
      return await PettyCashPaymentsReportService.getReport({
        companyId: companyId || undefined,
        employeeId: employeeId || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        page: 1,
        limit: 100000,
      });
    } catch (error) {
      console.error("Export error:", error);
      return null;
    }
  };

  const exportExcel = async () => {
    const res = await getAllReportData();
    const allData = res?.data || [];

    if (!allData.length) {
      showMessage("No data to export");
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = {};
    ws["!merges"] = [];
    const thin = { style: "thin" };
    const border = { top: thin, bottom: thin, left: thin, right: thin };
    const center = { horizontal: "center", vertical: "center", wrapText: true };
    let row = 0;

    XLSX.utils.sheet_add_aoa(ws, [[activeCompany?.companyName || "Company"]], { origin: { r: row, c: 0 } });
    ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = { font: { bold: true, color: { rgb: "000000" }, sz: 12 } };
    XLSX.utils.sheet_add_aoa(ws, [[`Report Date: ${utility.formatExportDateTime(new Date())}`]], { origin: { r: row, c: 4 } });
    ws[XLSX.utils.encode_cell({ r: row, c: 4 })].s = { font: { bold: true, sz: 10 }, alignment: { horizontal: "right" } };
    row += 2;

    XLSX.utils.sheet_add_aoa(ws, [["Petty Cash Payments Report"]], { origin: { r: row, c: 0 } });
    ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 4 } });
    ws[XLSX.utils.encode_cell({ r: row, c: 0 })].s = {
      font: { bold: true, sz: 16, underline: true },
      alignment: center,
    };
    row += 2;

    XLSX.utils.sheet_add_aoa(ws, [
      ["Start Date:", startDate ? utility.formatDateDDMMMYYYY(startDate) : "-"],
      ["End Date:", endDate ? utility.formatDateDDMMMYYYY(endDate) : "-"],
      ["Employee Name:", getEmployeeLabel()],
    ], { origin: { r: row, c: 0 } });

    ws["!merges"].push(
      { s: { r: row, c: 1 }, e: { r: row, c: 2 } },
      { s: { r: row + 1, c: 1 }, e: { r: row + 1, c: 2 } },
      { s: { r: row + 2, c: 1 }, e: { r: row + 2, c: 2 } }
    );

    for (let r = row; r < row + 3; r++) {
      for (let c = 0; c < 3; c++) {
        const cell = XLSX.utils.encode_cell({ r, c });
        if (!ws[cell]) {
          ws[cell] = { t: "s", v: "" };
        }
        ws[cell].s = {
          font: { bold: c === 0, sz: 10 },
          alignment: { horizontal: "left", vertical: "center" },
          border,
        };
      }
    }
    row += 4;

    const headers = ["Sr.No", "Employee Name", "Total Payments", "Total Exp", "Balance"];
    const headerRowIndex = row;
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: { r: row, c: 0 } });
    headers.forEach((_, colIndex) => {
      ws[XLSX.utils.encode_cell({ r: row, c: colIndex })].s = {
        font: { bold: true, sz: 11 },
        alignment: center,
        border,
      };
    });
    row++;

    allData.forEach((item, index) => {
      const totPay = Number(item.totalPayments || 0);
      const totExp = Number(item.totalExp || 0);
      const balVal = Number(item.balance || 0);
      const rowData = [
        index + 1,
        item.employeeName || "-",
        totPay,
        totExp,
        balVal,
      ];
      XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: { r: row, c: 0 } });
      rowData.forEach((_, colIndex) => {
        const cell = XLSX.utils.encode_cell({ r: row, c: colIndex });
        ws[cell].s = {
          alignment: { horizontal: colIndex >= 2 ? "right" : colIndex === 0 ? "center" : "left", vertical: "center" },
          border,
        };
        if (colIndex === 0) {
          ws[cell].t = "n";
          ws[cell].v = index + 1;
          ws[cell].z = "0";
        }
        if (colIndex >= 2) {
          ws[cell].t = "n";
          ws[cell].v = colIndex === 2 ? totPay : colIndex === 3 ? totExp : balVal;
          ws[cell].z = "#,##0.00;(#,##0.00)";
        }
      });
      row++;
    });

    ws["!autofilter"] = {
      ref: XLSX.utils.encode_range({
        s: { r: headerRowIndex, c: 0 },
        e: { r: row - 1, c: headers.length - 1 }
      })
    };

    const sumPay = Number(res.summary?.totalPayments || 0);
    const sumExp = Number(res.summary?.totalExp || 0);
    const sumBal = Number(res.summary?.balance || 0);
    XLSX.utils.sheet_add_aoa(ws, [["", "Total(s)", sumPay, sumExp, sumBal]], { origin: { r: row, c: 0 } });
    for (let c = 0; c < 5; c++) {
      const cell = XLSX.utils.encode_cell({ r: row, c });
      if (!ws[cell]) {
        ws[cell] = { t: "s", v: "" };
      }
      ws[cell].s = {
        font: { bold: true },
        alignment: {
          horizontal: c === 0 ? "center" : c >= 1 ? "right" : "left",
          vertical: "center",
        },
        border,
      };
      if (c >= 2) {
        ws[cell].t = "n";
        ws[cell].v = c === 2 ? sumPay : c === 3 ? sumExp : sumBal;
        ws[cell].z = "#,##0.00;(#,##0.00)";
      }
    }

    ws["!cols"] = [{ wch: 18 }, { wch: 28 }, { wch: 18 }, { wch: 18 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(wb, ws, "PettyCashPayments");
    XLSX.writeFile(wb, "PettyCashPaymentsReport.xlsx");
  };

  const exportPdf = async () => {
    const res = await getAllReportData();
    const allData = res?.data || [];

    if (!allData.length) {
      showMessage("No data to export");
      return;
    }

    const doc = new jsPDF("landscape");
    const pdfLogo = await getPdfLogoDataUrl(logo);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageMargin = 14;
    const right = pageWidth - pageMargin;
    const left = pageMargin;
    const companyName = activeCompany?.companyName || "Company";
    const title = "Petty Cash Payments Report";

    const drawHeader = (pageNumber) => {
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.2);

      doc.setFont(undefined, "bold");
      doc.setFontSize(12);
      const companyNameWidth = doc.getTextWidth(companyName);
      const logoWidth = 5;
      const logoHeight = 10;
      const logoX = left + 6 + (companyNameWidth - logoWidth) / 2;
      const logoY = 8;

      doc.addImage(pdfLogo, "PNG", logoX, logoY, logoWidth, logoHeight);
      doc.text(companyName, left + 6, 23);

      doc.setFontSize(10);
      doc.setFont(undefined, "normal");
      doc.text(`Report Date: ${utility.formatExportDateTime(new Date())}`, right, 23, { align: "right" });

      doc.setFontSize(18);
      doc.setFont(undefined, "bold");
      doc.text(title, pageWidth / 2, 35, { align: "center" });

      const titleWidth = doc.getTextWidth(title);
      doc.setLineWidth(0.3);
      doc.line(pageWidth / 2 - titleWidth / 2, 37, pageWidth / 2 + titleWidth / 2, 37);
    };

    drawHeader(1);
    let yPosition = 48;
    doc.setFontSize(10);
    doc.setFont(undefined, "bold");
    doc.text("Start Date:", 14, yPosition);
    doc.setFont(undefined, "normal");
    doc.text(startDate ? utility.formatDateDDMMMYYYY(startDate) : "-", 39, yPosition);
    doc.setFont(undefined, "bold");
    doc.text("End Date:", 14, yPosition + 7);
    doc.setFont(undefined, "normal");
    doc.text(endDate ? utility.formatDateDDMMMYYYY(endDate) : "-", 39, yPosition + 7);
    doc.setFont(undefined, "bold");
    doc.text("Employee Name:", 14, yPosition + 14);
    doc.setFont(undefined, "normal");
    doc.text(getEmployeeLabel(), 44, yPosition + 14);

    // autoTable(doc, {
    //   startY: yPosition + 25,
    //   head: [["Sr.No", "Employee Name", "Total Payments", "Total Exp", "Balance"]],
    //   body: allData.map((row, index) => [
    //     index + 1,
    //     row.employeeName || "-",
    //     formatAmount(row.totalPayments),
    //     formatAmount(row.totalExp),
    //     formatAmount(row.balance),
    //   ]),
    //   foot: [["", "Total(s)", formatAmount(res.summary?.totalPayments), formatAmount(res.summary?.totalExp), formatAmount(res.summary?.balance)]],
    //   showFoot: "lastPage",
    //   theme: "grid",
    //   styles: { fontSize: 9, lineColor: [0, 0, 0], lineWidth: 0.15, textColor: [0, 0, 0] },
    //   headStyles: { fillColor: [220, 220, 220], textColor: [0, 0, 0], fontStyle: "bold", halign: "center" },
    //   footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: "bold" },
    //   columnStyles: {
    //     0: { halign: "center" },
    //     1: { halign: "right" },
    //     2: { halign: "right" },
    //     3: { halign: "right" },
    //     4: { halign: "right" },
    //   },

    //   didParseCell: (data) => {
    //     if (data.section === "foot" && [1, 2, 3, 4].includes(data.column.index)) {
    //       data.cell.styles.halign = "right";
    //     }
    //   },
    // });

    autoTable(doc, {
      startY: yPosition + 25,

      head: [["Sr.No", "Employee Name", "Total Payments", "Total Exp", "Balance"]],

      body: allData.map((row, index) => [
        index + 1,
        row.employeeName || "-",
        formatAmount(row.totalPayments),
        formatAmount(row.totalExp),
        formatAmount(row.balance),
      ]),

      foot: [[
        "",
        "Total(s)",
        formatAmount(res.summary?.totalPayments),
        formatAmount(res.summary?.totalExp),
        formatAmount(res.summary?.balance)
      ]],

      showFoot: "lastPage",

      theme: "grid",

      styles: {
        fontSize: 9,
        lineColor: [0, 0, 0],
        lineWidth: 0.15,
        textColor: [0, 0, 0]
      },

      headStyles: {
        fillColor: [230, 230, 230], // Light Gray
        textColor: [0, 0, 0], // Dark text
        fontStyle: "bold",
        halign: "center"
      },

      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold"
      },

      columnStyles: {
        0: { halign: "right" },
        1: { halign: "left" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" }
      },

      margin: {
        top: 40,
        left: 14,
        right: 14
      },

      // didParseCell: (data) => {
      //   if (
      //     data.section === "foot" &&
      //     [1, 2, 3, 4].includes(data.column.index)
      //   ) {
      //     data.cell.styles.halign = "right";
      //   }
      // },

      didParseCell: (data) => {
        if (data.section === "foot") {

          if (data.column.index === 1) {
            data.cell.styles.halign = "left";
          }

          if ([2, 3, 4].includes(data.column.index)) {
            data.cell.styles.halign = "right";
          }
        }
      },

      // didDrawPage: function (data) {

      //   // First page already has header
      //   if (data.pageNumber === 1) return;

      //   doc.setFont(undefined, "bold");
      //   doc.setFontSize(12);

      //   const companyNameWidth =
      //     doc.getTextWidth(companyName);

      //   const logoX =
      //     14 + (companyNameWidth - 9) / 2;

      //   doc.addImage(
      //     logo,
      //     "PNG",
      //     logoX,
      //     10,
      //     9,
      //     15
      //   );

      //   doc.text(
      //     companyName,
      //     14,
      //     32
      //   );

      //   const title =
      //     "Petty Cash Payments Report";

      //   doc.setFontSize(18);

      //   const textWidth =
      //     doc.getTextWidth(title);

      //   const centerX =
      //     (pageWidth - textWidth) / 2;

      //   doc.text(
      //     title,
      //     centerX,
      //     30
      //   );

      //   doc.setLineWidth(0.3);

      //   doc.line(
      //     centerX,
      //     32,
      //     centerX + textWidth,
      //     32
      //   );

      //   doc.setFontSize(10);

      //   doc.text(
      //     `Report Date: ${utility.formatExportDateTime(new Date())}`,
      //     pageWidth - 14,
      //     18,
      //     { align: "right" }
      //   );
      // }

      didDrawPage: function (data) {
        if (data.pageNumber === 1) return;
        drawHeader(data.pageNumber);
      }
    });


    const drawPageNumber = (pageNumber, totalPages) => {
      doc.setFont(undefined, "normal");
      doc.setFontSize(8);
      const text = totalPages
        ? `Page ${pageNumber} of ${totalPages}`
        : `Page ${pageNumber}`;
      doc.text(text, pageWidth / 2, pageHeight - 7, { align: "center" });
    };

    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      drawPageNumber(i, pageCount);
    }

    doc.save("PettyCashPaymentsReport.pdf");
  };

  return (
    <>
      <Breadcrumb pageTitle="Petty Cash Payments Report" secondTitle="Reports" />

      <MessageDialog message={message} type={msgType} setMessage={setMessage} setMsgType={setMsgType} />

      <div className="card p-4 bg-white shadow-sm">


        <div className="row g-3 mb-3">

          <div className="col-md-3">
            <label className="form-label">Employee Name</label>
            <select className="form-select" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
              <option value="">All</option>
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {`${employee.firstName || ""} ${employee.lastName || ""}`.trim()}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label">Start Date</label>
            <input type="date" className="form-control" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>

          <div className="col-md-3">
            <label className="form-label">End Date</label>
            <input type="date" className="form-control" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>

          {/* <div className="col-md-3">
            <label className="form-label">Employee Name</label>
            <select className="form-select" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
              <option value="">All</option>
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {`${employee.firstName || ""} ${employee.lastName || ""}`.trim()}
                </option>
              ))}
            </select>
          </div> */}
        </div>

        <div className="mb-3 d-flex justify-content-end align-items-center">

          <div className="d-flex gap-2">
            <button className="btn btn-secondary" onClick={handleClearFilters}>
              <i className="ti ti-refresh me-1"></i>
              Clear
            </button>
            <button className="btn btn-primary" onClick={() => loadReport(1)} disabled={loading}>
              <i className="ti ti-search me-1"></i>
              {loading ? "Searching..." : "View Report"}
            </button>
          </div>
        </div>
        <hr></hr>
        <div className="mb-3 d-flex justify-content-end align-items-center">
          <div className="d-flex gap-2">
            <button className="btn btn-success" onClick={exportPdf} disabled={loading}>
              <i className="ti ti-file-type-pdf me-1"></i>
              Export PDF
            </button>
            <button className="btn btn-success" onClick={exportExcel} disabled={loading}>
              <i className="ti ti-file-spreadsheet me-1"></i>
              Export Excel
            </button>
          </div>

        </div>
        <div className="table-responsive">

          <table className="table table-bordered table-striped mb-0">
            <thead className="table-light">
              <tr>
                <th>Sr.No</th>
                <th>Employee Name</th>
                <th className="text-end">Total Payments</th>
                <th className="text-end">Total Exp</th>
                <th className="text-end">Balance</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center"><div className="spinner-border text-primary" /></td>
                </tr>
              ) : reports.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center">No data found</td>
                </tr>
              ) : (
                <>
                  {reports.map((row, index) => (
                    <tr key={row.employeeId || index}>
                      <td>{(page - 1) * limit + index + 1}</td>
                      <td>{row.employeeName || "-"}</td>
                      <td className="text-end">{formatAmount(row.totalPayments)}</td>
                      <td className="text-end">{formatAmount(row.totalExp)}</td>
                      <td className="text-end">{formatAmount(row.balance)}</td>
                    </tr>
                  ))}
                  {page === totalPages && (
                    <tr className="fw-bold">
                      <td></td>
                      <td className="text-end">Total(s)</td>
                      <td className="text-end">{formatAmount(summary.totalPayments)}</td>
                      <td className="text-end">{formatAmount(summary.totalExp)}</td>
                      <td className="text-end">{formatAmount(summary.balance)}</td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>Total Records: <strong>{totalRecords}</strong></div>
              <div className="d-flex gap-2">
                <button className="btn btn-sm btn-secondary" disabled={page === 1} onClick={() => loadReport(page - 1)}>Prev</button>
                {[...Array(totalPages)]
                  .map((_, i) => i + 1)
                  .filter((p) => p >= page - 2 && p <= page + 2)
                  .map((p) => (
                    <button key={p} className={`btn btn-sm ${page === p ? "btn-primary" : "btn-outline-primary"}`} onClick={() => loadReport(p)}>
                      {p}
                    </button>
                  ))}
                <button className="btn btn-sm btn-secondary" disabled={page === totalPages} onClick={() => loadReport(page + 1)}>Next</button>
              </div>
            </div>
          )}
        </div>
      </div >
    </>
  );
}
